import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Draggable from 'react-draggable';
import logoImage from '../assets/images/logo.png'; // Import the logo
import { FaDatabase, FaPuzzlePiece, FaLaptopCode, FaBrain } from 'react-icons/fa'; // Import icons

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  position: relative;
  background: transparent;
  color: #fff;
  overflow: hidden;
  isolation: isolate;
`;

const ContentContainer = styled.div`
  position: relative;
  z-index: 50;
  padding: 0 5%;
  pointer-events: auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const TechnologySection = styled.div`
  margin-top: 100px; /* Space for the fixed navbar */
  padding: 2rem 0;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
`;

const SectionTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.79);
  text-align: center;
`;

const IntroText = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 1);
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem auto;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.9);
`;

const ArchitectureDiagram = styled.div`
  margin: 3rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DiagramImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.5);
`;

const ArchitectureDescription = styled.div`
  margin: 2rem 0;
`;

const ComponentSection = styled.div`
  margin: 4rem 0;
`;

const ComponentTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: white;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
`;

const TechnologyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const TechnologyCard = styled.div`
  background: rgba(30, 30, 30, 0.7);
  border-radius: 10px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: white;
`;

const CardDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
`;

// Update the DiagramLayout to stack vertically on mobile
const DiagramLayout = styled.div`
  display: grid;
  grid-template-columns: 6fr 3fr;
  gap: 1.5rem;
  margin: 3rem 0;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

// Make the InteractiveDiagramContainer responsive
const InteractiveDiagramContainer = styled.div`
  position: relative;
  width: 100%;
  height: 850px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
  
  @media (max-width: 768px) {
    height: 500px; /* Shorter height on mobile */
  }
`;

const DragInstructions = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  color: white;
  z-index: 10;
  pointer-events: none;
  white-space: nowrap;
`;

// Update the ResetButton styling to ensure it stays on top
const ResetButton = styled.button`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 100; /* Increase z-index to ensure it stays on top */
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

// Update the InfoPanel to be always expanded and maintain a fixed size
const InfoPanel = styled.div`
  background: rgba(20, 20, 20, 0.9); /* Darker background for better visibility */
  backdrop-filter: blur(10px);
  border-radius: 10px;
  padding: 1.5rem;
  height: 850px; /* Fixed height to match the InteractiveDiagramContainer */
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
  
  @media (max-width: 1024px) {
    margin-top: 1rem;
    height: 500px; /* Match mobile height */
  }
`;

const InfoTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: white;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
`;

const InfoDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2rem;
`;

// Update the TechnologyList to allow scrolling within the panel
const TechnologyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  flex: 1; /* Allow this to take remaining space */
  padding-right: 0.5rem; /* Add some padding for the scrollbar */
`;

// Update the DraggableCanvas SVG dimensions
const DraggableCanvas = styled.div`
  width: 1800px;
  height: 1400px;
  transform-origin: 0 0;
`;

const NodeCircle = styled.circle`
  cursor: pointer;
  transition: r 0.3s, fill 0.3s;
  
  &:hover {
    r: ${props => props.r * 1.1};
    fill-opacity: 0.9;
  }
`;

// Update the NodeLabel component to be more visible on mobile
const NodeLabel = styled.text`
  text-anchor: middle;
  fill: white;
  font-size: ${props => props.isCentral ? '14px' : '12px'};
  font-weight: ${props => props.isCentral ? 'bold' : 'normal'};
  pointer-events: none;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
  dy: ${props => props.isCentral ? '35px' : '25px'};
  
  @media (max-width: 768px) {
    font-size: ${props => props.isCentral ? '12px' : '10px'};
    dy: ${props => props.isCentral ? '30px' : '22px'};
  }
`;

const NodeConnection = styled.path`
  stroke-width: ${props => props.isHighlighted ? 2 : 1};
  stroke-opacity: ${props => props.isHighlighted ? 0.9 : 0.6};
  fill: none;
`;

// Add a styled component for the logo in the node
const NodeLogo = styled.image`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
`;

// Update the NodeIcon component to use SVG positioning instead of foreignObject
const NodeIcon = styled.g`
  color: white;
  font-size: ${props => props.size * 0.5}px;
`;

// Add mobile-friendly controls for the diagram
const MobileControls = styled.div`
  display: none; /* Hide the mobile controls completely */
`;

const MobileControlButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

// Add a styled component for the Key Technologies heading with margin
const KeyTechnologiesHeading = styled.h3`
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  color: white;
`;

function Technology() {
  const [position, setPosition] = useState({ x: -500, y: -300 });
  const [zoom, setZoom] = useState(1);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  
  // Define the diagram path
  const diagramPath = "/architecture-diagram.png";
  
  // Define the nodes for the diagram
  const nodes = [
    // Core platform (center) - with black background
    { id: 'geos', x: 900, y: 700, r: 50, label: 'Geo OS', color: '#000000', type: 'central', isLogo: true },
    
    // Four main components around the center in a more balanced arrangement
    { id: 'databases', x: 900, y: 500, r: 35, label: 'Databases', color: '#000000', type: 'central', icon: 'database' },
    { id: 'foundation', x: 650, y: 700, r: 35, label: 'Foundation Models', color: '#000000', type: 'central', icon: 'brain' },
    { id: 'modules', x: 900, y: 900, r: 35, label: 'Modularized Functions', color: '#000000', type: 'central', icon: 'puzzle' },
    { id: 'applications', x: 1150, y: 700, r: 35, label: 'Applications', color: '#000000', type: 'central', icon: 'laptop' },
    
    // Data sources connected to Databases - in a more arc-like arrangement
    { id: 'weather', x: 750, y: 400, r: 25, label: 'Weather Radars', color: '#ffffff', type: 'source' },
    { id: 'satellite', x: 900, y: 350, r: 25, label: 'Satelites Images', color: '#ffffff', type: 'source' },
    { id: 'aircraft', x: 1050, y: 400, r: 25, label: 'Aircraft', color: '#ffffff', type: 'source' },
    { id: 'ship', x: 1150, y: 450, r: 25, label: 'Ship', color: '#ffffff', type: 'source' },
    { id: 'thirdparty', x: 650, y: 450, r: 25, label: '3rd Party', color: '#ffffff', type: 'source' },
    
    // Foundation Models outputs - in a more natural flow
    { id: 'forecast', x: 500, y: 650, r: 25, label: 'Prediction', color: '#ffffff', type: 'source' },
    { id: 'translate', x: 500, y: 750, r: 25, label: 'Translation', color: '#ffffff', type: 'source' },
    
    // Modularized Functions outputs - with better spacing to prevent text overlap
    { id: 'navigation', x: 750, y: 1000, r: 25, label: 'Navigation', color: '#ffffff', type: 'source' },
    { id: 'route', x: 900, y: 1050, r: 25, label: 'Route Planning', color: '#ffffff', type: 'source' },
    { id: 'optimization', x: 1050, y: 1000, r: 25, label: 'Constraint Optimization', color: '#ffffff', type: 'source' },
    { id: 'traffic', x: 650, y: 950, r: 25, label: 'Traffic Prediction', color: '#ffffff', type: 'source' },
    { id: 'agent', x: 1150, y: 950, r: 25, label: 'Agent Orchestration', color: '#ffffff', type: 'source' },
    
    // Applications outputs - in a more natural flow
    { id: 'gui', x: 1300, y: 650, r: 25, label: 'Interative GUI', color: '#ffffff', type: 'source' },
    { id: 'efb', x: 1300, y: 750, r: 25, label: 'Pilot EFB', color: '#ffffff', type: 'source' },
    { id: 'dispatcher', x: 1250, y: 850, r: 25, label: 'Dispatch Platform', color: '#ffffff', type: 'source' },
  ];
  
  // Define connections between nodes
  const connections = [
    // Connect Geo OS to the four main components
    { from: 'geos', to: 'databases', style: 'solid' },
    { from: 'geos', to: 'foundation', style: 'solid' },
    { from: 'geos', to: 'modules', style: 'solid' },
    { from: 'geos', to: 'applications', style: 'solid' },
    
    // Connect data sources to Databases
    { from: 'databases', to: 'weather', style: 'solid' },
    { from: 'databases', to: 'satellite', style: 'solid' },
    { from: 'databases', to: 'aircraft', style: 'solid' },
    { from: 'databases', to: 'ship', style: 'solid' },
    { from: 'databases', to: 'thirdparty', style: 'dotted' },
    
    // Connect Foundation Models to its outputs
    { from: 'foundation', to: 'forecast', style: 'solid' },
    { from: 'foundation', to: 'translate', style: 'solid' },
    
    // Connect Modularized Functions to its outputs including new nodes
    { from: 'modules', to: 'navigation', style: 'solid' },
    { from: 'modules', to: 'route', style: 'solid' },
    { from: 'modules', to: 'optimization', style: 'solid' },
    { from: 'modules', to: 'traffic', style: 'solid' },
    { from: 'modules', to: 'agent', style: 'solid' },
    
    // Connect Applications to its outputs
    { from: 'applications', to: 'gui', style: 'solid' },
    { from: 'applications', to: 'efb', style: 'solid' },
    { from: 'applications', to: 'dispatcher', style: 'solid' },
  ];
  
  // Component data for the info panel
  const componentData = {
    geos: {
      title: "Geo OS",
      description: "The core operating system that integrates geospatial data processing, foundation models, modular functions, and applications.",
      technologies: [
        { title: "Distributed Computing", description: "Scalable architecture for processing large volumes of geospatial data" },
        { title: "Real-time Processing", description: "Low-latency data handling for time-sensitive operations" },
        { title: "API Integration", description: "Unified interfaces for connecting with various data sources and applications" }
      ]
    },
    databases: {
      title: "Databases",
      description: "Centralized storage and management of geospatial data from various sources.",
      technologies: [
        { title: "Spatial Databases", description: "Optimized storage for geographic information" },
        { title: "Time-series Data", description: "Efficient handling of temporal geospatial data" },
        { title: "Data Fusion", description: "Integration of multiple data sources into unified datasets" }
      ]
    },
    foundation: {
      title: "Foundation Models",
      description: "Advanced AI models that process and interpret geospatial data for various applications.",
      technologies: [
        { title: "Geospatial Machine Learning", description: "Specialized algorithms for geographic data analysis" },
        { title: "Predictive Analytics", description: "Forecasting based on historical and real-time data" },
        { title: "Natural Language Processing", description: "Converting geospatial data to human-readable insights" }
      ]
    },
    modules: {
      title: "Modules",
      description: "Specialized components that provide specific functionality to the GeoSpatios platform.",
      technologies: [
        { title: "Agent Orchestration", description: "Coordinates autonomous agents operating in complex geospatial environments." },
        { title: "Navigation Systems", description: "Advanced pathfinding and routing algorithms for optimal movement through space." },
        { title: "Constraint Optimization", description: "Solves complex multi-variable problems with spatial and temporal constraints." },
        { title: "Situation Prediction", description: "Forecasts future states of dynamic environments using historical and real-time data." }
      ]
    },
    applications: {
      title: "Applications",
      description: "End-user solutions built on the GeoSpatios platform that solve specific industry challenges.",
      technologies: [
        { title: "Vessel Optimization", description: "Maximizes efficiency of maritime operations through optimal routing and scheduling." },
        { title: "Flight Planning", description: "Comprehensive flight path optimization considering weather, airspace, and operational constraints." },
        { title: "Air Traffic Prediction", description: "Forecasts air traffic patterns to improve safety and efficiency in congested airspace." },
        { title: "Visualization Tools", description: "Advanced visualization Platform that transform complex data into intuitive, actionable insights." }
      ]
    }
  };
  
  // Add data for all other nodes
  nodes.forEach(node => {
    if (!componentData[node.id]) {
      componentData[node.id] = {
        title: node.label,
        description: `${node.label} is an important component in the GeoSpatios architecture.`,
        technologies: [
          { title: "Integration", description: `${node.label} integrates seamlessly with the GeoSpatios platform.` }
        ]
      };
    }
  });
  
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 2));
  };
  
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 0.5));
  };
  
  const handleReset = () => {
    setPosition({ x: -500, y: -300 });
    setZoom(1);
  };
  
  const handleNodeClick = (nodeId) => {
    console.log("Node clicked:", nodeId); // Add debugging
    setSelectedNode(nodeId === selectedNode ? null : nodeId);
  };
  
  // Inside the Technology component, add these functions for mobile controls
  const handlePanUp = () => {
    setPosition(prev => ({ x: prev.x, y: prev.y + 50 }));
  };

  const handlePanDown = () => {
    setPosition(prev => ({ x: prev.x, y: prev.y - 50 }));
  };

  const handlePanLeft = () => {
    setPosition(prev => ({ x: prev.x + 50, y: prev.y }));
  };

  const handlePanRight = () => {
    setPosition(prev => ({ x: prev.x - 50, y: prev.y }));
  };
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <PageContainer>
      <Navbar />
      <ContentContainer>
        <TechnologySection>
          <SectionTitle>Our Technology</SectionTitle>
          <IntroText>
            At the heart of our platform lies a sophisticated architecture designed to handle the complexities of autonomous operations.
          </IntroText>
          
          <DiagramLayout>
            <InteractiveDiagramContainer>
              <DragInstructions>
                Click and drag to move the diagram
              </DragInstructions>
              <ResetButton onClick={handleReset}>
                Reset View
              </ResetButton>
              <Draggable
                position={position}
                onStop={(e, data) => setPosition({ x: data.x, y: data.y })}
                onDrag={(e, data) => e.stopPropagation()}
                cancel=".clickable-node"
              >
                <DraggableCanvas style={{ transform: `scale(${zoom})` }}>
                  <svg width="1800" height="1400" viewBox="0 0 1800 1400">
                    {/* Draw connections */}
                    {connections.map((conn, index) => {
                      const fromNode = nodes.find(n => n.id === conn.from);
                      const toNode = nodes.find(n => n.id === conn.to);
                      const highlighted = 
                        hoveredNode === conn.from || 
                        hoveredNode === conn.to || 
                        selectedNode === conn.from || 
                        selectedNode === conn.to;
                      
                      // Calculate a control point for the curve
                      const midX = (fromNode.x + toNode.x) / 2;
                      const midY = (fromNode.y + toNode.y) / 2;
                      const dx = toNode.x - fromNode.x;
                      const dy = toNode.y - fromNode.y;
                      const normalX = -dy * 0.3; // Perpendicular to the line
                      const normalY = dx * 0.3;  // Perpendicular to the line
                      
                      // Create a curved path
                      const path = `M ${fromNode.x} ${fromNode.y} Q ${midX + normalX} ${midY + normalY} ${toNode.x} ${toNode.y}`;
                      
                      return (
                        <NodeConnection
                          key={index}
                          d={path}
                          stroke="white"
                          strokeDasharray={conn.style === 'dotted' ? "5,5" : ""}
                          isHighlighted={highlighted}
                        />
                      );
                    })}
                    
                    {/* Draw nodes */}
                    {nodes.map((node) => (
                      <g 
                        key={node.id}
                        className="clickable-node"
                        onMouseEnter={() => setHoveredNode(node.id)}
                        onMouseLeave={() => setHoveredNode(null)}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log("Node clicked with ID:", node.id);
                          setSelectedNode(prevSelected => prevSelected === node.id ? null : node.id);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <NodeCircle
                          cx={node.x}
                          cy={node.y}
                          r={node.r}
                          fill={node.color}
                          stroke={node.id === selectedNode ? 'white' : 'rgba(255, 255, 255, 0.5)'}
                          strokeWidth={node.id === selectedNode ? 3 : 1}
                        />
                        
                        {/* Add logo for GeoOS node */}
                        {node.isLogo && (
                          <NodeLogo
                            href={logoImage}
                            x={node.x - node.r * 0.7}
                            y={node.y - node.r * 0.7}
                            size={node.r * 1.4}
                            preserveAspectRatio="xMidYMid meet"
                          />
                        )}
                        
                        {/* Add icons for the main nodes */}
                        {node.icon === 'database' && (
                          <NodeIcon size={node.r}>
                            <svg 
                              x={node.x - node.r * 0.4} 
                              y={node.y - node.r * 0.4} 
                              width={node.r * 0.8} 
                              height={node.r * 0.8} 
                              viewBox="0 0 472 512" 
                              fill="white"
                            >
                              <path d="M448 80v48c0 44.2-100.3 80-224 80S0 172.2 0 128V80C0 35.8 100.3 0 224 0S448 35.8 448 80zM393.2 214.7c20.8-7.4 39.9-16.9 54.8-28.6V288c0 44.2-100.3 80-224 80S0 332.2 0 288V186.1c14.9 11.8 34 21.2 54.8 28.6C99.7 230.7 159.5 240 224 240s124.3-9.3 169.2-25.3zM0 346.1c14.9 11.8 34 21.2 54.8 28.6C99.7 390.7 159.5 400 224 400s124.3-9.3 169.2-25.3c20.8-7.4 39.9-16.9 54.8-28.6V432c0 44.2-100.3 80-224 80S0 476.2 0 432V346.1z"/>
                            </svg>
                          </NodeIcon>
                        )}
                        
                        {node.icon === 'puzzle' && (
                          <NodeIcon size={node.r}>
                            <svg 
                              x={node.x - node.r * 0.4} 
                              y={node.y - node.r * 0.4} 
                              width={node.r * 0.8} 
                              height={node.r * 0.8} 
                              viewBox="0 0 496 512" 
                              fill="white"
                            >
                              <path d="M519.442 288.651c-41.519 0-59.5 31.593-82.058 31.593C377.409 320.244 432 144 432 144s-196.288 80-196.288-3.297c0-35.827 36.288-46.25 36.288-85.985C272 19.216 243.885 0 210.539 0c-34.654 0-66.366 18.891-66.366 56.346 0 41.364 31.711 59.277 31.711 81.75C175.885 207.719 0 166.758 0 166.758v333.237s178.635 41.047 178.635-28.662c0-22.473-40-40.107-40-81.471 0-37.456 29.25-56.346 63.577-56.346 33.673 0 61.788 19.216 61.788 54.717 0 39.735-36.288 50.158-36.288 85.985 0 60.803 129.675 25.73 181.23 25.73 0 0-34.725-120.101 25.827-120.101 35.962 0 46.423 36.152 86.308 36.152C556.712 416 576 387.99 576 354.443c0-34.199-18.962-65.792-56.558-65.792z"/>
                            </svg>
                          </NodeIcon>
                        )}
                        
                        {node.icon === 'laptop' && (
                          <NodeIcon size={node.r}>
                            <svg 
                              x={node.x - node.r * 0.4} 
                              y={node.y - node.r * 0.4} 
                              width={node.r * 0.8} 
                              height={node.r * 0.8} 
                              viewBox="0 0 640 512" 
                              fill="white"
                            >
                              <path d="M128 32C92.7 32 64 60.7 64 96V352h64V96H512V352h64V96c0-35.3-28.7-64-64-64H128zM19.2 384C8.6 384 0 392.6 0 403.2C0 445.6 34.4 480 76.8 480H563.2c42.4 0 76.8-34.4 76.8-76.8c0-10.6-8.6-19.2-19.2-19.2H19.2z"/>
                            </svg>
                          </NodeIcon>
                        )}
                        
                        {node.icon === 'brain' && (
                          <NodeIcon size={node.r}>
                            <svg 
                              x={node.x - node.r * 0.4} 
                              y={node.y - node.r * 0.4} 
                              width={node.r * 0.8} 
                              height={node.r * 0.8} 
                              viewBox="0 0 512 512" 
                              fill="white"
                            >
                              <path d="M184 0c30.9 0 56 25.1 56 56V456c0 30.9-25.1 56-56 56c-28.9 0-52.7-21.9-55.7-50.1c-5.2 1.4-10.7 2.1-16.3 2.1c-35.3 0-64-28.7-64-64c0-7.4 1.3-14.6 3.6-21.2C21.4 367.4 0 338.2 0 304c0-31.9 18.7-59.5 45.8-72.3C37.1 220.8 32 207 32 192c0-30.7 21.6-56.3 50.4-62.6C80.8 123.9 80 118 80 112c0-29.9 20.6-55.1 48.3-62.1C131.3 21.9 155.1 0 184 0zM328 0c28.9 0 52.6 21.9 55.7 49.9c27.8 7 48.3 32.1 48.3 62.1c0 6-.8 11.9-2.4 17.4c28.8 6.2 50.4 31.9 50.4 62.6c0 15-5.1 28.8-13.8 39.7C493.3 244.5 512 272.1 512 304c0 34.2-21.4 63.4-51.6 74.8c2.3 6.6 3.6 13.8 3.6 21.2c0 35.3-28.7 64-64 64c-5.6 0-11.1-.7-16.3-2.1c-3 28.2-26.8 50.1-55.7 50.1c-30.9 0-56-25.1-56-56V56c0-30.9 25.1-56 56-56zM184 80c-8.8 0-16 7.2-16 16s7.2 16 16 16H328c8.8 0 16-7.2 16-16s-7.2-16-16-16H184zm0 64c-8.8 0-16 7.2-16 16s7.2 16 16 16H328c8.8 0 16-7.2 16-16s-7.2-16-16-16H184zm0 64c-8.8 0-16 7.2-16 16s7.2 16 16 16H328c8.8 0 16-7.2 16-16s-7.2-16-16-16H184zm0 64c-8.8 0-16 7.2-16 16s7.2 16 16 16H328c8.8 0 16-7.2 16-16s-7.2-16-16-16H184zm0 64c-8.8 0-16 7.2-16 16s7.2 16 16 16H328c8.8 0 16-7.2 16-16s-7.2-16-16-16H184z"/>
                            </svg>
                          </NodeIcon>
                        )}
                        
                        <NodeLabel
                          x={node.x}
                          y={node.y + node.r + 20}
                          isCentral={node.type === 'central'}
                        >
                          {node.label}
                        </NodeLabel>
                      </g>
                    ))}
                  </svg>
                </DraggableCanvas>
              </Draggable>
            </InteractiveDiagramContainer>
            
            <InfoPanel>
              {selectedNode ? (
                <>
                  <InfoTitle>{componentData[selectedNode].title}</InfoTitle>
                  <InfoDescription>{componentData[selectedNode].description}</InfoDescription>
                  
                  <KeyTechnologiesHeading>Key Technologies:</KeyTechnologiesHeading>
                  <TechnologyList>
                    {componentData[selectedNode].technologies.map((tech, index) => (
                      <TechnologyCard key={index}>
                        <CardTitle>{tech.title}</CardTitle>
                        <CardDescription>{tech.description}</CardDescription>
                      </TechnologyCard>
                    ))}
                  </TechnologyList>
                </>
              ) : (
                <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                  <p>Click on a node in the diagram to view details</p>
                </div>
              )}
            </InfoPanel>
          </DiagramLayout>
        </TechnologySection>
      </ContentContainer>
    </PageContainer>
  );
}

export default Technology; 